import { flagKeySchema, variantKeySchema, type FeatureFlagKey } from '@galapagos/product-analytics';

import type { FeatureFlagProvider, FeatureFlagSnapshot, FeatureFlagValue } from '../types';

export type FeatureFlagDefaults = Readonly<Record<string, FeatureFlagValue>>;

function validateDefaults(defaults: FeatureFlagDefaults): FeatureFlagDefaults {
  for (const [key, value] of Object.entries(defaults)) {
    flagKeySchema.parse(key);
    if (typeof value !== 'boolean') variantKeySchema.parse(value);
  }
  return defaults;
}

export function createDefaultFeatureFlagProvider(
  inputDefaults: FeatureFlagDefaults,
  options: { now?: () => Date; snapshotVersion?: string } = {},
): FeatureFlagProvider {
  const defaults = validateDefaults(inputDefaults);
  const now = options.now ?? (() => new Date());
  const snapshotVersion = options.snapshotVersion ?? '1';

  return {
    evaluateAll: async ({ keys }) =>
      defaultFeatureFlagSnapshot(defaults, keys, now(), snapshotVersion),
  };
}

export function defaultFeatureFlagSnapshot(
  defaults: FeatureFlagDefaults,
  keys: readonly FeatureFlagKey[],
  evaluatedAt: Date,
  snapshotVersion: string,
): FeatureFlagSnapshot {
  const values: Record<string, FeatureFlagValue> = {};
  const evaluations: FeatureFlagSnapshot['evaluations'] extends Readonly<infer T> ? T : never = {};

  for (const key of keys) {
    const value = defaults[key];
    if (value === undefined) throw new Error(`Unknown registered feature flag: ${key}`);
    values[key] = value;
    evaluations[key] = { value, source: 'default', usedDefault: true };
  }

  return {
    values,
    evaluations,
    evaluatedAt: evaluatedAt.toISOString(),
    snapshotVersion,
  };
}
